import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { Formik } from "formik";
import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import ConfirmationDialog from "@components/ConfirmationDialog";
import CrudTable from "@components/CrudTable";
import GroupNumberSelector from "@components/GroupNumberSelector";
import GroupNumbersImporter from "@components/GroupNumbersImporter";
import PageBody from "@components/PageBody";
import PageControl from "@components/PageControl";
import GroupService from "@services/Group";
import AdminPageTemplate from "@templates/AdminPage";
import { formatPhone } from "@utils/form";

const useStyles = makeStyles(() => ({
  formContent: {
    paddingTop: "12px",
    paddingBottom: "14px",
  },
  numbersTableDividir: {
    marginTop: "14px",
    marginBottom: "2px",
  },
  numbersControl: {
    marginTop: "8px",
    textAlign: "right",
  },
  numbersControlButton: {
    marginLeft: "16px",
  },
}));

const defaultConfirmationDialogData = {
  open: false,
  title: "",
  message: "",
  onClose: () => {},
  onDisagree: () => {},
  onAgree: () => {},
};

const numbersTableColumns = [
  {
    id: "number",
    label: "Número de Telefone",
  },
  {
    id: "menu",
    type: "menu",
    label: "",
    align: "right",
  },
];

function generateIDRegexp() {
  return new RegExp("^[a-zA-Z0-9_-]+$", "g");
}

function validateID(value) {
  const re = generateIDRegexp();
  return re.test(value);
}

function Group(props) {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();

  const accountId = useSelector((state) => state.account.id);
  const listId = params.id || "";

  const [loading, setLoading] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [group, setGroup] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContactRows, setSelectedContactRows] = useState([]);
  const [openGroupNumberSelector, setOpenGroupNumberSelector] = useState(false);
  const [openGroupNumbersImporter, setOpenGroupNumbersImporter] =
    useState(false);
  const [confirmationDialogData, setConfirmationDialogData] = useState(
    defaultConfirmationDialogData
  );

  function isNewMode() {
    return !params.id;
  }

  function isEditMode() {
    return !!params.id;
  }

  function closeConfirmation() {
    setConfirmationDialogData({ ...confirmationDialogData, open: false });
    setTimeout(() => {
      setConfirmationDialogData(defaultConfirmationDialogData);
    }, 500);
  }

  function showContactDeletionConfirmation(contact) {
    setConfirmationDialogData({
      open: true,
      title: "Excluir número de telefone",
      message: `Você tem certeza que deseja excluir o número de telefone "${formatPhone(
        contact.mobile
      )}"?`,
      onClose: () => {
        closeConfirmation();
      },
      onDisagree: () => {
        closeConfirmation();
      },
      onAgree: () => {
        deleteContact(contact);
        closeConfirmation();
      },
    });
  }

  function showMultipleContactsDeletionConfirmation(rows) {
    setConfirmationDialogData({
      open: true,
      title: "Excluir números de telefone",
      message: `Você tem certeza que deseja excluir os números de telefone selecionados?`,
      onClose: () => {
        closeConfirmation();
      },
      onDisagree: () => {
        closeConfirmation();
      },
      onAgree: () => {
        deleteMultipleContacts(rows);
        closeConfirmation();
      },
    });
  }

  function createContactRowMenu(contact) {
    return [
      {
        label: "Excluir",
        icon: DeleteIcon,
        onClick: () => showContactDeletionConfirmation(contact),
      },
    ];
  }

  function createContactRow(contact) {
    return {
      id: contact.mobile,
      number: formatPhone(contact.mobile),
      menu: createContactRowMenu(contact),
    };
  }

  function redirectToGroup(listId) {
    history.push(`/grupos/${listId}`);
  }

  function redirectToGroupsPage() {
    history.push("/grupos");
  }

  function loadGroup() {
    setLoading(true);
    GroupService.find(params.id)
      .then((data) => {
        setGroup(data);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao carregar dados do grupo!");
        setLoading(false);
      });
  }

  function loadContacts() {
    setLoadingContacts(true);
    setContacts([]);
    setSelectedContactRows([]);

    GroupService.listContacts(params.id)
      .then((data) => {
        setContacts(data);
        setLoadingContacts(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao carregar lista de contatos!");
        setLoadingContacts(false);
      });
  }

  function deleteContact(contact) {
    GroupService.deleteContact(params.id, contact.mobile)
      .then(() => {
        toast.success(
          `Número de telefone "${formatPhone(contact.mobile)}" excluído!`
        );
        loadContacts();
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `Erro ao excluir número de telefone "${formatPhone(contact.mobile)}"!`
        );
      });
  }

  function deleteMultipleContacts(rows) {
    const requests = rows.map((row) =>
      GroupService.deleteContact(params.id, row.id)
    );
    Promise.all(requests)
      .then(() => {
        toast.success("Números de telefone excluídos!");
        loadContacts();
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao excluir números de telefone!");
      });
  }

  function saveGroup(values) {
    setLoading(true);
    GroupService.save({
      accountId: accountId,
      listId: values.id,
      name: values.name,
    })
      .then((group) => {
        if (isNewMode()) {
          toast.success("Grupo cadastrado!");
          redirectToGroup(group.listId);
        } else if (isEditMode()) {
          toast.success("Grupo salvo!");
          redirectToGroupsPage();
        }
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        if (isNewMode()) {
          toast.error("Erro ao cadastrar grupo!");
        } else if (isEditMode()) {
          toast.error("Erro ao salvar o grupo!");
        }
        setLoading(false);
      });
  }

  function handleClickAddNumber() {
    setOpenGroupNumberSelector(true);
  }

  function handleSuccessAddNumber() {
    loadContacts();
    setOpenGroupNumberSelector(false);
  }

  function handleCancelAddNumber() {
    setOpenGroupNumberSelector(false);
  }

  function handleClickImportNumbers() {
    setOpenGroupNumbersImporter(true);
  }

  function handleCancelNumbersImport() {
    setOpenGroupNumbersImporter(false);
  }

  function handleSuccessNumbersImport(fileData) {
    setOpenGroupNumbersImporter(false);
  }

  function handleSelectContactRow(rows) {
    setSelectedContactRows(rows);
  }

  function handleRefreshContactsTable() {
    loadContacts();
  }

  const contactRows = useMemo(() => {
    return contacts.map((contact) => createContactRow(contact));
  }, [contacts]);

  const breadcrumbs = useMemo(() => {
    const breadcrumbs = [...props.breadcrumbs];
    if (isNewMode()) {
      breadcrumbs[2].label = "Novo";
    } else {
      breadcrumbs[2].label = params.id;
    }
    return breadcrumbs;
  }, [props.breadcrumbs]);

  function handleClickCancel() {
    redirectToGroupsPage();
  }

  function handleClickRegister(values) {
    saveGroup(values);
  }

  function handleDeleteContactRows(rows) {
    showMultipleContactsDeletionConfirmation(rows);
  }

  useEffect(() => {
    if (isEditMode()) {
      loadGroup();
      loadContacts();
    }
  }, []);

  const initialValues = {
    id: "",
    name: "",
  };
  if (isEditMode() && group) {
    initialValues.id = group.listId;
    initialValues.name = group.name;
  }

  const validationSchema = Yup.object().shape({
    id: Yup.string()
      .required("Campo requerido")
      .test("", "Possui caracteres inválidos", (value) => {
        if (value === undefined) {
          return true;
        }
        return validateID(value);
      })
      .max(64, "Número máximo de 64 caracteres"),
    name: Yup.string()
      .required("Campo requerido")
      .max(256, "Número máximo de 256 caracteres"),
  });

  return (
    <AdminPageTemplate breadcrumbs={breadcrumbs}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        enableReinitialize
      >
        {(context) => {
          const { errors, touched, isValid, values, handleChange, handleBlur } =
            context;
          return (
            <PageBody title="Grupo">
              <div className={classes.formContent}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      value={values.id}
                      variant="outlined"
                      fullWidth
                      label="Código de Identificação"
                      name="id"
                      required
                      disabled={isEditMode()}
                      helperText={touched.id ? errors.id : ""}
                      error={touched.id && Boolean(errors.id)}
                      onChange={(e) => {
                        if (
                          e.target.value === "" ||
                          validateID(e.target.value)
                        ) {
                          handleChange(e);
                        }
                      }}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      value={values.name}
                      variant="outlined"
                      fullWidth
                      label="Nome"
                      name="name"
                      required
                      helperText={touched.name ? errors.name : ""}
                      error={touched.name && Boolean(errors.name)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                </Grid>
              </div>
              <PageControl>
                <Button
                  color="default"
                  disabled={loading}
                  onClick={handleClickCancel}
                >
                  Cancelar
                </Button>
                <Button
                  color="primary"
                  disabled={!isValid || loading}
                  onClick={() => handleClickRegister(values)}
                >
                  {isNewMode() && "Cadastrar"}
                  {isEditMode() && "Salvar"}
                </Button>
              </PageControl>
            </PageBody>
          );
        }}
      </Formik>

      {isEditMode() && (
        <PageBody title="Lista de Contatos">
          <div className={classes.formContent}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CrudTable
                  columns={numbersTableColumns}
                  rows={contactRows}
                  loading={loadingContacts}
                  filter={true}
                  filterFields={[
                    {
                      type: "text",
                      name: "number",
                      label: "Número de Telefone",
                    },
                  ]}
                  control={true}
                  refresh={true}
                  rowsSelection={true}
                  selectedRows={selectedContactRows}
                  onRefreshRows={handleRefreshContactsTable}
                  onSelectRow={handleSelectContactRow}
                  onDeleteRows={handleDeleteContactRows}
                />
                <div className={classes.numbersControl}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.numbersControlButton}
                    startIcon={<CloudUploadIcon />}
                    onClick={handleClickImportNumbers}
                  >
                    Importar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.numbersControlButton}
                    onClick={handleClickAddNumber}
                  >
                    Adicionar
                  </Button>
                </div>
              </Grid>
            </Grid>

            <GroupNumberSelector
              open={openGroupNumberSelector}
              listId={params.id}
              onCancel={handleCancelAddNumber}
              onSuccess={handleSuccessAddNumber}
            />

            <GroupNumbersImporter
              open={openGroupNumbersImporter}
              listId={listId}
              onCancel={handleCancelNumbersImport}
              onSuccess={handleSuccessNumbersImport}
            />
          </div>
        </PageBody>
      )}

      <ConfirmationDialog {...confirmationDialogData} />
    </AdminPageTemplate>
  );
}

Group.propTypes = {
  breadcrumbs: PropTypes.array,
};

Group.defaultProps = {
  breadcrumbs: [],
};

export default Group;
